import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as ArrowIcon } from "../../assets/icons/svg/arrow-twoside-icon.svg";
import { ReactComponent as PauseIcon } from "../../assets/icons/svg/pause-icon.svg";
import DeletePopup from "../ui/DeletePopup";
import ImageCard from "./DashbordActiveScreensImageCard";

interface Creative {
  id: string;
  name: string;
  endDate: string;
  type: string;
  image: string;
}

interface ScreenCardProps {
  screen: {
    id: string;
    image: string;
    title: string;
    creatives: number;
    tags: string[];
    creativesList?: Creative[];
  };
}

const ScreenCard: React.FC<ScreenCardProps> = ({ screen }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showPausePopup, setShowPausePopup] = useState(false);
  const [selectedCreative, setSelectedCreative] = useState<Creative | null>(
    null
  );

  const cardRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const mockCreatives: Creative[] = Array.from(
    { length: screen.creatives },
    (_, index) => ({
      id: `creative-${index + 1}`,
      name: "online-delivery.png",
      endDate: "01 Mar, 2025",
      type: index === 3 ? "video/mp4" : "image/jpeg",
      image: screen.image,
    })
  );

  const creativeItems = screen.creativesList || mockCreatives;

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handlePause = (creative: Creative, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCreative(creative);
    setShowPausePopup(true);
  };

  const handleConfirmPause = () => {
    if (selectedCreative) {
      console.log(`Pausing creative: ${selectedCreative.id}`);
      // Implement pause logic here
    }
    setShowPausePopup(false);
    setSelectedCreative(null);
  };

  const handleCancelPause = () => {
    setShowPausePopup(false);
    setSelectedCreative(null);
  };

  return (
    <>
      {/* Overlay for pause popup */}
      {showPausePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-40 pointer-events-auto"></div>
      )}

      {/* Main Content Wrapper */}
      <div
        className={`relative w-full transition-opacity ${
          showPausePopup ? "pointer-events-none  select-none" : ""
        }`}
        ref={cardRef}
      >
        <div
          className={`relative w-full flex flex-col rounded-[24px] border border-gray-200 border-opacity-50 shadow-sm transition-all ${
            isDropdownOpen ? "z-20 bg-white" : "bg-transparent"
          }`}
        >
          {/* Top Card */}
          <div className="h-[114px] sm:h-[100px] flex justify-between items-center p-4 sm:p-5 lg:p-[22px]">
            <div className="flex items-center gap-8 sm:gap-6">
              <ImageCard imageUrl={screen.image} />
              <div className="flex flex-col">
                <div className="flex items-center gap-6 sm:gap-3 flex-wrap">
                  <h3 className="text-lg sm:text-base font-semibold">
                    {screen.title}
                  </h3>
                  <span className="w-[1px] h-4 bg-gray-300"></span>
                  <span className="text-sm sm:text-xs text-gray-500 whitespace-nowrap">
                    {screen.creatives.toString().padStart(2, "0")} creatives
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 sm:gap-1">
                  {screen.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="bg-gray-100 text-sm sm:text-xs text-gray-700 px-3 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Dropdown Toggle */}
            <div
              className="absolute top-3 right-3 w-[28px] h-[28px] p-[3px] flex items-center justify-center cursor-pointer"
              onClick={toggleDropdown}
            >
              <ArrowIcon
                className={`transition-transform duration-200 ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
                title={isDropdownOpen ? "Hide Details" : "View Details"}
              />
            </div>
          </div>

          {/* Separator */}
          {isDropdownOpen && (
            <div className="absolute bottom-0 left-0 right-0 px-4">
              <div className="w-full h-[1px] bg-gray-200"></div>
            </div>
          )}

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white rounded-[24px] border border-gray-200 border-t-0 shadow-lg z-30 overflow-hidden">
              <div className="max-h-[260px] overflow-y-auto">
                {creativeItems.map((creative, index) => (
                  <div
                    key={creative.id}
                    className="flex items-center justify-between py-2 px-4 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 w-4">
                        {index + 1}.
                      </span>
                      <img
                        src={creative.image}
                        alt={creative.name}
                        className="w-8 h-8 object-cover rounded"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {creative.name}
                        </span>
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>Ends on: {creative.endDate}</span>
                          <span>{creative.type}</span>
                        </div>
                      </div>
                    </div>
                    <button onClick={(e) => handlePause(creative, e)}>
                      <PauseIcon
                        className="text-red-500 hover:opacity-80 transition-opacity"
                        width={16}
                        height={16}
                        title="Pause Creative"
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pause Confirmation Modal */}
      {showPausePopup && selectedCreative && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <DeletePopup
            title={`Pause Creative: ${selectedCreative.name}`}
            message="Are you sure you want to pause this creative? This action will stop the creative from being displayed on the screen."
            onCancel={handleCancelPause}
            onConfirm={handleConfirmPause}
          />
        </div>
      )}
    </>
  );
};

export default ScreenCard;
